import { useTranslation } from 'react-i18next';

import { CurrentPlansElement, SubscriptionActionsElement } from '@shared/dream-components';

import { useSubscriptionActionsContext } from '~/context/SubscriptionManagement';

import { getRevokedAccessBannerText } from '../../../_utils/getRevokedAccessBannerText';

/**
 * Renders the current-plans card and its nested subscription-action buttons. Everything —
 * plan display data and every opener — comes from `SubscriptionActionsProvider` via context.
 * Pure UI, no props, no action logic.
 */
export const CurrentPlansSection = () => {
  const { t } = useTranslation();
  const {
    currentPlans,
    showEmptyPlanState,
    isLoadingPlans,
    canShowCurrentPlans,
    hasGroupAndIndividualOverlap,
    groupSubscriptionsEnabled,
    groupAccessRevokedFrom,
    hasActivePaidTier,
    onClickChangePlan,
    onClickCancelPlan,
    onClickUnpausePlan,
    onClickLogin,
    onClickUnpause,
    onClickResubscribe,
    onClickUnsubscribe,
    onClickReportUnwantedSignup,
    onClickUpgrade,
  } = useSubscriptionActionsContext();

  /*
    Show current plans section for free tier subscribers if they are not logged in, as they
    should always have an option to (un/re)subscribe. Premium users aren't shown this block as
    unsubscribing cancels their paid subscription, thus they should authorize before changing.
  */
  if (!canShowCurrentPlans) {
    return null;
  }

  return (
    <CurrentPlansElement
      element={{
        type: 'currentPlans',
        attrs: {
          data: {
            // Single plan: badge in section header. Multi plan: badge per card.
            statusBadge:
              currentPlans.length === 1 && currentPlans[0]?.badgeType
                ? {
                    type: currentPlans[0].badgeType,
                    text: currentPlans[0].status,
                    tooltipText: currentPlans[0].tooltipText,
                  }
                : undefined,
            overlapWarning: hasGroupAndIndividualOverlap ? t('billing.current_plan.overlap_warning') : undefined,
            revokedAccess: getRevokedAccessBannerText(groupSubscriptionsEnabled, groupAccessRevokedFrom, t),
            plans: currentPlans,
          },
        },
      }}
      showEmptyState={showEmptyPlanState}
      isLoading={isLoadingPlans}
      translations={{
        'billing.current_plan.title': t('billing.current_plan.title'),
        'billing.current_plan.pause_tooltip_prefix': t('billing.current_plan.pause_tooltip_prefix'),
        'billing.current_plan.cancel_tooltip': t('billing.current_plan.cancel_tooltip'),
        'billing.current_plan.unpause_tooltip': t('billing.current_plan.unpause_tooltip'),
        'billing.current_plan.cancel_in_progress_tooltip': t('billing.current_plan.cancel_in_progress_tooltip'),
        'billing.current_plan.change_plan_tooltip': t('billing.current_plan.change_plan_tooltip'),
        'billing.current_plan.change_plan_in_progress_tooltip': t(
          'billing.current_plan.change_plan_in_progress_tooltip',
        ),
        'billing.summary.empty_state': t('billing.summary.empty_state'),
        'billing.plans_modal.current_plan_button': t('billing.plans_modal.current_plan_button'),
        'billing.plans_modal.switch_to_plan_button': t('billing.plans_modal.switch_to_plan_button'),
        'shared.cta.cancel_paid_subscription': t('shared.cta.cancel_paid_subscription'),
        'billing.plan.change_plan': t('billing.plan.change_plan'),
        'billing.plan.unpause_plan': t('billing.plan.unpause_plan'),
      }}
      onClickCancelPlan={onClickCancelPlan}
      onClickUnpausePlan={onClickUnpausePlan}
      onClickChangePlan={onClickChangePlan}
    >
      <SubscriptionActionsElement
        element={{
          type: 'subscriptionActions',
        }}
        onClickLogin={onClickLogin}
        onClickUnpause={onClickUnpause}
        onClickResubscribe={onClickResubscribe}
        onClickUnsubscribe={onClickUnsubscribe}
        onClickReportUnwantedSignup={onClickReportUnwantedSignup}
        onClickUpgradePlan={onClickUpgrade}
        unsubscribeEmphasis={hasActivePaidTier ? 'link' : 'button'}
        translations={{
          'shared.cta.unsubscribe': t(
            hasActivePaidTier ? 'shared.cta.unsubscribe_and_end_paid' : 'shared.cta.unsubscribe',
          ),
          'shared.cta.resubscribe': t('shared.cta.resubscribe'),
          'shared.cta.unpause': t('shared.cta.unpause'),
          'shared.cta.login_to_continue': t('shared.cta.login_to_continue'),
          'billing.free_plan.upgrade_plan': t('billing.free_plan.upgrade_plan'),
          'shared.cta.report_abuse': t('shared.cta.report_abuse'),
        }}
      >
        {null}
      </SubscriptionActionsElement>
    </CurrentPlansElement>
  );
};
